import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getRegistrationStatus } from '../services/api';

const RequestStatus = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [status, setStatus] = useState('pending'); // 'pending', 'approved', 'denied'
  const [requestData, setRequestData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState('');
  const [email, setEmail] = useState('');

  // Maps the backend user status to this page's display states
  const mapStatus = (backendStatus) => {
    if (backendStatus === 'active') return 'approved';
    if (backendStatus === 'inactive') return 'denied';
    return 'pending';
  };

  // Fetches the real registration status for the given email
  const fetchStatus = async (emailToCheck, appType) => {
    if (!emailToCheck) return;
    setLoading(true);
    setErrorMsg('');
    try {
      const res = await getRegistrationStatus(emailToCheck, appType);
      setRequestData(res.data);
      const mapped = mapStatus(res.data.status);
      setStatus(mapped);
      // Clean up the stored pending email once the user is approved so the
      // Login page's "Check status" shortcut disappears once they can log in.
      if (mapped === 'approved') {
        localStorage.removeItem('orgpass_pending_email');
      }
    } catch (err) {
      if (err.response && err.response.status === 404) {
        setErrorMsg('No registration request was found for this email address.');
      } else {
        setErrorMsg('Failed to check your request status. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Reads the email and application_type from the URL (or localStorage) on first load and fetches status
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const emailParam = searchParams.get('email') || localStorage.getItem('orgpass_pending_email');
    const appTypeParam = searchParams.get('application_type') || null;
    if (emailParam) {
      setEmail(emailParam);
      fetchStatus(emailParam, appTypeParam);
    } else {
      setLoading(false);
    }
  }, [location]);

  const config = {
    pending: {
      icon: (
        <div className="w-16 h-16 bg-yellow-50 rounded-full flex items-center justify-center mb-6">
          <svg className="w-8 h-8 text-yellow-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
      ),
      title: 'Request Submitted',
      desc: 'Your access request has been received and is currently awaiting review by the coordinator.',
      buttonText: 'Back to Homepage',
      buttonAction: () => navigate('/login'),
      steps: [
        { name: 'Request\nSubmitted', state: 'completed' },
        { name: 'Pending\nReview', state: 'current' },
        { name: 'Access\nGranted', state: 'upcoming' },
        { name: 'Login\nNow', state: 'upcoming' },
      ],
      progressWidth: '33%',
      progressColor: '#10B981'
    },
    approved: {
      icon: (
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-6">
          <svg className="w-8 h-8 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
          </svg>
        </div>
      ),
      title: 'Access approved!',
      desc: 'Congratulations! Your organization has been granted access to OrgPass.\nYou may now log in and begin submitting your compliance documents.',
      buttonText: 'Proceed to Login',
      buttonAction: () => navigate('/login'),
      steps: [
        { name: 'Request\nSubmitted', state: 'completed' },
        { name: 'Pending\nReview', state: 'completed' },
        { name: 'Access\nGranted', state: 'completed' },
        { name: 'Login\nNow', state: 'upcoming_green' },
      ],
      progressWidth: '100%',
      progressColor: '#10B981'
    },
    denied: {
      icon: (
        <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-6">
          <svg className="w-8 h-8 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </div>
      ),
      title: 'Access denied!',
      desc: 'Your organization has not been granted access. The submitted details did not meet\nthe required criteria for approval.',
      buttonText: 'Back to Homepage',
      buttonAction: () => navigate('/login'),
      steps: [
        { name: 'Request\nSubmitted', state: 'completed' },
        { name: 'Pending\nReview', state: 'completed' },
        { name: 'Access\nGranted', state: 'error' },
        { name: 'Login\nNow', state: 'upcoming' },
      ],
      progressWidth: '66%',
      progressColor: '#EF4444'
    }
  };

  const currentConfig = config[status];

  // Loading state — shown while the first status check is in flight
  if (loading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center p-8"
        style={{ background: '#054334' }}
      >
        <p className="text-white text-sm">Checking your request status...</p>
      </div>
    );
  }

  // When no requestData is loaded or on error — provide a lookup form
  if (!requestData || errorMsg) {
    return (
      <div
        className="min-h-screen flex items-center justify-center p-8"
        style={{
          background: '#054334',
          backgroundImage: 'url(assets/login_image.png)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed',
        }}
      >
        <div className="absolute inset-0" style={{ background: 'rgba(6, 60, 40, 0.82)' }} />
        <div className="relative z-10 w-full max-w-[500px] bg-white rounded-[32px] lg:rounded-[40px] shadow-2xl overflow-hidden p-8 lg:p-10 flex flex-col items-center text-center">
          <div className="w-14 h-14 bg-emerald-50 rounded-full flex items-center justify-center mb-5">
            <svg className="w-7 h-7 text-[#086C53]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <h1 className="text-[22px] font-extrabold text-gray-900 mb-2">Check Application Status</h1>
          <p className="text-[13px] text-gray-500 mb-6">
            Enter the institutional email you used when applying for access.
          </p>

          {errorMsg && (
            <div className="w-full mb-5 p-3 bg-red-50 border border-red-200 rounded-xl text-red-600 text-[12px] font-medium text-center">
              {errorMsg}
            </div>
          )}

          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (email) fetchStatus(email);
            }}
            className="w-full space-y-4"
          >
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="e.g. president@slsu.edu.ph or you@gmail.com"
              required
              className="w-full bg-[#f8fafc] border border-gray-200 rounded-xl px-4 py-3 text-[14px] text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#086C53]/30 transition text-center"
            />
            <button
              type="submit"
              disabled={!email}
              className="w-full py-3.5 rounded-xl text-[14px] font-bold text-white transition-colors shadow-md disabled:opacity-50"
              style={{ background: '#054334' }}
            >
              Check Status
            </button>
          </form>

          <div className="mt-6 flex justify-center items-center space-x-4 text-[12px] text-gray-500">
            <button
              onClick={() => navigate('/login')}
              className="text-[#086C53] font-semibold hover:underline"
            >
              ← Back to Login
            </button>
            <span>•</span>
            <button
              onClick={() => navigate('/signup')}
              className="text-[#086C53] font-semibold hover:underline"
            >
              Sign Up
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="relative min-h-screen flex items-center justify-center py-10 px-8"
      style={{
        background: '#054334',
        backgroundImage: 'url(assets/login_image.png)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed',
      }}
    >
      <div className="fixed inset-0" style={{ background: 'rgba(6, 60, 40, 0.82)', zIndex: 0 }} />

      <div className="relative z-10 w-full max-w-[1200px] bg-white rounded-[40px] shadow-2xl overflow-hidden p-10 flex flex-col items-center" style={{ minHeight: '650px' }}>

        {/* Top Left Logo */}
        <div className="absolute top-10 left-10 flex items-center space-x-2">
          <img src="assets/osas-logo.png" alt="OrgPass" className="w-6 h-6" onError={(e) => { e.target.style.display = 'none'; }} />
          <span className="font-bold text-gray-800 text-[16px]">OrgPass</span>
        </div>

        {/* Check Again — only useful while pending, re-fetches the real status */}
        {status === 'pending' && (
          <div className="absolute top-10 right-10">
            <button
              onClick={() => fetchStatus(email)}
              className="text-xs px-3 py-1.5 rounded-lg border border-gray-200 bg-gray-50 hover:bg-gray-100 text-gray-600 font-semibold transition-colors"
            >
              Check Again
            </button>
          </div>
        )}

        <div className="flex-1 flex flex-col items-center justify-center w-full max-w-3xl mt-12">

          {currentConfig.icon}

          <h1 className="text-[32px] font-extrabold text-gray-900 mb-2">{currentConfig.title}</h1>
          <p className="text-[13px] text-gray-600 mb-12 text-center whitespace-pre-line leading-relaxed">
            {currentConfig.desc}
          </p>

          {/* Stepper */}
          <div className="w-full max-w-2xl mb-14">
            <div className="relative flex justify-between items-center w-full">
              {/* Background Line */}
              <div className="absolute top-1/2 left-0 w-full h-[3px] bg-gray-200 -translate-y-1/2 z-0"></div>

              {/* Active Line */}
              <div
                className="absolute top-1/2 left-0 h-[3px] -translate-y-1/2 z-0 transition-all duration-500"
                style={{
                  width: currentConfig.progressWidth,
                  backgroundColor: currentConfig.progressColor,
                  background: status === 'denied' ? 'linear-gradient(to right, #10B981 50%, #EF4444 50%)' : currentConfig.progressColor
                }}
              ></div>

              {currentConfig.steps.map((step, idx) => {
                let circleClass = "";
                let icon = null;
                let textClass = "text-gray-500";

                if (step.state === 'completed') {
                  circleClass = "bg-[#10B981] border-2 border-[#10B981] text-white";
                  icon = <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>;
                  textClass = "text-gray-800";
                } else if (step.state === 'current') {
                  circleClass = "bg-yellow-400 border-2 border-yellow-400 text-white";
                  icon = <span className="font-bold text-sm">{idx + 1}</span>;
                  textClass = "text-gray-800 font-medium";
                } else if (step.state === 'error') {
                  circleClass = "bg-red-500 border-2 border-red-500 text-white";
                  icon = <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>;
                  textClass = "text-gray-800 font-medium";
                } else if (step.state === 'upcoming_green') {
                  circleClass = "bg-[#e0f5ee] border-2 border-[#10B981] text-[#10B981]";
                  icon = <span className="font-bold text-sm text-[#10B981]">Now</span>;
                  textClass = "text-gray-800 font-medium";
                } else {
                  circleClass = "bg-gray-200 border-2 border-gray-200 text-white";
                  icon = <span className="font-bold text-sm">{idx + 1}</span>;
                }

                return (
                  <div key={idx} className="relative z-10 flex flex-col items-center">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center transition-colors duration-300 ${circleClass}`}>
                      {icon}
                    </div>
                    <span className={`absolute top-14 text-center text-[11px] whitespace-pre-line mt-2 ${textClass}`}>
                      {step.name}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Details Card — real data from the backend */}
          <div className="w-full max-w-2xl bg-[#f8fafc] rounded-2xl p-8 mb-10 border border-gray-100">
            <h3 className="text-[12px] font-bold text-gray-400 uppercase tracking-wider mb-5">Your Request Details</h3>

            <div className="space-y-3">
              <div className="flex justify-between items-center py-2 border-b border-gray-100/50">
                <span className="text-[13px] text-gray-500 font-medium">
                  {requestData?.application_type === 'offcampus' ? 'Activity Name' : 'Organization'}
                </span>
                <span className="text-[13px] text-gray-800 font-medium text-right">
                  {requestData?.activity_name || requestData?.organization_name || 'N/A'}
                </span>
              </div>
              {requestData?.application_type !== 'offcampus' && (
                <div className="flex justify-between items-center py-2 border-b border-gray-100/50">
                  <span className="text-[13px] text-gray-500 font-medium">Acronym</span>
                  <span className="text-[13px] text-gray-800 font-medium text-right">
                    {requestData?.organization_acronym || 'N/A'}
                  </span>
                </div>
              )}
              <div className="flex justify-between items-center py-2 border-b border-gray-100/50">
                <span className="text-[13px] text-gray-500 font-medium">Campus/College</span>
                <span className="text-[13px] text-gray-800 font-medium text-right">{requestData?.organization_college || 'N/A'}</span>
              </div>
              {requestData?.application_type !== 'offcampus' && requestData?.organization_category && (
                <div className="flex justify-between items-center py-2 border-b border-gray-100/50">
                  <span className="text-[13px] text-gray-500 font-medium">Type of Organization</span>
                  <span className="text-[13px] text-gray-800 font-medium text-right">{requestData.organization_category}</span>
                </div>
              )}
              {requestData?.application_type !== 'offcampus' && requestData?.president_contact && (
                <div className="flex justify-between items-center py-2 border-b border-gray-100/50">
                  <span className="text-[13px] text-gray-500 font-medium">President Contact</span>
                  <span className="text-[13px] text-gray-800 font-medium text-right">{requestData.president_contact}</span>
                </div>
              )}
              {requestData?.application_type !== 'offcampus' && requestData?.adviser_name && (
                <div className="flex justify-between items-center py-2 border-b border-gray-100/50">
                  <span className="text-[13px] text-gray-500 font-medium">Adviser</span>
                  <span className="text-[13px] text-gray-800 font-medium text-right">{requestData.adviser_name}</span>
                </div>
              )}
              {requestData?.application_type !== 'offcampus' && requestData?.total_members !== null && requestData?.total_members !== undefined && (
                <div className="flex justify-between items-center py-2 border-b border-gray-100/50">
                  <span className="text-[13px] text-gray-500 font-medium">Total Members</span>
                  <span className="text-[13px] text-gray-800 font-medium text-right">{requestData.total_members}</span>
                </div>
              )}
              <div className="flex justify-between items-center py-2 border-b border-gray-100/50">
                <span className="text-[13px] text-gray-500 font-medium">Email</span>
                <span className="text-[13px] text-gray-800 font-medium text-right">{requestData?.slsu_email}</span>
              </div>
              <div className="flex justify-between items-center py-2">
                <span className="text-[13px] text-gray-500 font-medium">Submitted</span>
                <span className="text-[13px] text-gray-800 font-medium text-right">
                  {requestData?.submitted_at
                    ? new Date(requestData.submitted_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
                    : 'N/A'}
                </span>
              </div>
            </div>
          </div>

          <button
            onClick={currentConfig.buttonAction}
            className="px-28 py-3.5 rounded-xl text-[14px] font-bold text-white transition-colors shadow-md"
            style={{ background: '#054334' }}
            onMouseEnter={(e) => e.currentTarget.style.background = '#065440'}
            onMouseLeave={(e) => e.currentTarget.style.background = '#054334'}
          >
            {currentConfig.buttonText}
          </button>

        </div>
      </div>
    </div>
  );
};

export default RequestStatus;